import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { projectStore } from '../lib/projectStore';
import { organizationService, Organization } from '../services/organizationService';
import { OrgPermissions } from '../types/permissions';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  currentOrg: Organization | null;
  orgRole: 'owner' | 'admin' | 'member' | null;
  orgRoleId: string | null;
  permissions: OrgPermissions | null;
  authError: string | null;          // set when sign-in is blocked (no org membership)
  clearAuthError: () => void;
  signUp: (email: string, password: string, fullName: string, orgName: string) => Promise<{ error: AuthError | Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  refreshOrg: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser]               = useState<User | null>(null);
  const [session, setSession]         = useState<Session | null>(null);
  const [loading, setLoading]         = useState(true);
  const [currentOrg, setCurrentOrg]   = useState<Organization | null>(null);
  const [orgRole, setOrgRole]         = useState<'owner' | 'admin' | 'member' | null>(null);
  const [orgRoleId, setOrgRoleId]     = useState<string | null>(null);
  const [permissions, setPermissions] = useState<OrgPermissions | null>(null);
  const [authError, setAuthError]     = useState<string | null>(null);

  const clearAuthError = () => setAuthError(null);

  // clearOrgState — wipes org identity.
  //
  // `clearProject` defaults to false: a transient membership lookup failure
  // (token refresh on tab focus, network blip) must NOT silently throw away
  // the user's active project selection. Only a real sign-out — where the
  // next user of this browser must not inherit the previous one's filter —
  // passes `true`.
  const clearOrgState = (clearProject = false) => {
    setCurrentOrg(null);
    setOrgRole(null);
    setOrgRoleId(null);
    setPermissions(null);
    orgStore.setOrgId(null);
    if (clearProject) projectStore.setProjectId(null);
  };

  // loadOrg — called on session restore (app reload). Does NOT block if no org found
  // because a network blip shouldn't lock users out of an existing account.
  const loadOrg = async () => {
    const membership = await organizationService.getMyMembership();
    if (!membership) { clearOrgState(); return; }

    orgStore.setOrgId(membership.org_id);
    setOrgRole(membership.role);
    setOrgRoleId(membership.role_id);
    setPermissions(membership.permissions);
    const org = await organizationService.getMyOrganization();
    setCurrentOrg(org);
  };

  // loadOrgOrBlock — called on fresh SIGNED_IN events.
  // If the signed-in user has no org membership, they didn't go through the
  // normal signup (e.g. random Google login) — sign them out immediately.
  const loadOrgOrBlock = async (signedInUser: User) => {
    // Guard first: AcceptInvite sets this token before calling signUp.
    // getMyMembership() internally calls getUser() which can auto-signout on
    // a fresh session — skip the check entirely while invite acceptance runs.
    if (localStorage.getItem('pendingInviteToken')) return;

    const membership = await organizationService.getMyMembership();
    if (!membership) {
      // Auto sign-out — user has no org
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      clearOrgState(true); // real sign-out — drop the project filter too
      const isGoogle = signedInUser.app_metadata?.provider === 'google';
      setAuthError(
        isGoogle
          ? 'Your Google account is not linked to any organization. Please ask your admin to invite you.'
          : 'No organization found for this account. Please contact your admin.'
      );
      return;
    }

    orgStore.setOrgId(membership.org_id);
    setOrgRole(membership.role);
    setOrgRoleId(membership.role_id);
    setPermissions(membership.permissions);
    const org = await organizationService.getMyOrganization();
    setCurrentOrg(org);
  };

  const refreshOrg = async () => { await loadOrg(); };

  const ensureUserProfile = async (u: User) => {
    try {
      const fullName = u.user_metadata?.full_name || '';
      const email = u.email || '';
      if (fullName || email) {
        await supabase
          .from('user_profiles')
          .upsert({
            id: u.id,
            full_name: fullName,
            email: email,
          });
      }
    } catch (err) {
      console.error('Failed to ensure user profile:', err);
    }
  };

  useEffect(() => {
    // Session restore on app load — lenient, doesn't block
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        ensureUserProfile(session.user);
        loadOrg().finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        ensureUserProfile(session.user);
        if (event === 'SIGNED_IN') {
          // Fresh sign-in — enforce org membership check
          loadOrgOrBlock(session.user).finally(() => setLoading(false));
        } else {
          // Token refresh, etc — just reload org silently
          loadOrg().finally(() => setLoading(false));
        }
      } else {
        // Signed out
        clearOrgState(true);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    orgName: string
  ): Promise<{ error: AuthError | Error | null }> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) return { error };
    if (!data.user) return { error: new Error('Signup failed: no user returned') };

    try {
      // SECURITY DEFINER RPC — works with or without an active session
      const { error: rpcError } = await supabase.rpc('create_organization_for_user', {
        p_user_id:  data.user.id,
        p_org_name: orgName,
      });
      if (rpcError) throw rpcError;
    } catch (orgError) {
      return { error: orgError as Error };
    }

    return { error: null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { error };
  };

  const signOut = async () => {
    clearOrgState(true);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{
      user, session, loading,
      currentOrg, orgRole, orgRoleId, permissions,
      authError, clearAuthError,
      signUp, signIn, signInWithGoogle, signOut,
      refreshOrg,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
